/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Check, ChevronLeft, ChevronDown, X, Lock } from 'lucide-react';

import { C, F } from './constants/theme';
import { IMAGES } from './constants/data';
import {
  Btn,
  Brandmark,
  Eyebrow,
  Heading,
  Fade,
  StripeTexture,
} from './components';
import Phase0Landing from './phases/Phase0Landing.jsx';
import Phase1Goals from './phases/Phase1Goals.jsx';
import Phase2Lifestyle from './phases/Phase2Lifestyle.jsx';
import Phase3Taste from './phases/Phase3Taste.jsx';
import Phase4Biomarkers from './phases/Phase4Biomarkers.jsx';
import Phase5Program from './phases/Phase5Program.jsx';
import Phase6PlanConfig from './phases/Phase6PlanConfig.jsx';
import Phase7Results from './phases/Phase7Results.jsx';
import Phase8Checkout from './phases/Phase8Checkout.jsx';
import Loading from 'core/ui/components/Loading';
import useUser from 'core/hooks/useUser';
import { LIST_HOMEPAGE_MEALS } from 'core/api/apollo/queries';

const BASE_PATH = '/onramp/quiz';
const ROUTE_PATHS = {
  home: BASE_PATH,
  loading: `${BASE_PATH}/loading`,
  results: `${BASE_PATH}/results`,
  onramp: {
    0: `${BASE_PATH}/build`,
    1: `${BASE_PATH}/goals`,
    2: `${BASE_PATH}/lifestyle`,
    3: {
      0: `${BASE_PATH}/taste/style`,
      1: `${BASE_PATH}/taste/cuisines`,
      2: `${BASE_PATH}/taste/proteins`,
      3: `${BASE_PATH}/taste/avoidances`,
    },
    4: {
      default: `${BASE_PATH}/biomarkers`,
      upload: `${BASE_PATH}/biomarkers/upload`,
      flag: `${BASE_PATH}/biomarkers/flag`,
    },
    5: {
      0: `${BASE_PATH}/program`,
      1: `${BASE_PATH}/program/size`,
    },
    6: `${BASE_PATH}/plan`,
    7: `${BASE_PATH}/splitresults`,
    8: {
      0: `${BASE_PATH}/checkout/account`,
      1: `${BASE_PATH}/checkout/shipping`,
      2: `${BASE_PATH}/checkout/payment`,
      3: `${BASE_PATH}/checkout/welcome`,
    },
  },
  checkout: {
    0: `${BASE_PATH}/checkout/account`,
    1: `${BASE_PATH}/checkout/shipping`,
    2: `${BASE_PATH}/checkout/payment`,
    3: `${BASE_PATH}/checkout/welcome`,
  },
};

const normalizePath = (path = '') => {
  const normalized = path.replace(/\/+$/, '');
  return normalized || '/';
};

const getRouteState = (pathname) => {
  const path = normalizePath(pathname);
  if (path === ROUTE_PATHS.home) return { mode: 'home' };
  if (path === ROUTE_PATHS.loading) return { mode: 'loading' };
  if (path === ROUTE_PATHS.results) return { mode: 'results' };

  if (path === ROUTE_PATHS.checkout[0]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 0 };
  if (path === ROUTE_PATHS.checkout[1]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 1 };
  if (path === ROUTE_PATHS.checkout[2]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 2 };
  if (path === ROUTE_PATHS.checkout[3]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 3 };

  if (path === ROUTE_PATHS.onramp[0]) return { mode: 'onramp', phase: 0, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[1]) return { mode: 'onramp', phase: 1, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[2]) return { mode: 'onramp', phase: 2, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[3][0]) return { mode: 'onramp', phase: 3, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[3][1]) return { mode: 'onramp', phase: 3, subStep: 1, bioOption: null };
  if (path === ROUTE_PATHS.onramp[3][2]) return { mode: 'onramp', phase: 3, subStep: 2, bioOption: null };
  if (path === ROUTE_PATHS.onramp[3][3]) return { mode: 'onramp', phase: 3, subStep: 3, bioOption: null };
  if (path === ROUTE_PATHS.onramp[4].default) return { mode: 'onramp', phase: 4, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[4].upload) return { mode: 'onramp', phase: 4, subStep: 0, bioOption: 'upload' };
  if (path === ROUTE_PATHS.onramp[4].flag) return { mode: 'onramp', phase: 4, subStep: 0, bioOption: 'flag' };
  if (path === ROUTE_PATHS.onramp[5][0]) return { mode: 'onramp', phase: 5, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[5][1]) return { mode: 'onramp', phase: 5, subStep: 1, bioOption: null };
  if (path === ROUTE_PATHS.onramp[6]) return { mode: 'onramp', phase: 6, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[7]) return { mode: 'onramp', phase: 7, subStep: 0, bioOption: null };
  if (path === ROUTE_PATHS.onramp[8][0]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 0 };
  if (path === ROUTE_PATHS.onramp[8][1]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 1 };
  if (path === ROUTE_PATHS.onramp[8][2]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 2 };
  if (path === ROUTE_PATHS.onramp[8][3]) return { mode: 'onramp', phase: 8, subStep: 0, bioOption: null, checkoutStep: 3 };

  return null;
};

// ═══════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════
export default function MethodologyV6() {
  const history = useHistory();
  const location = useLocation();

  // Navigation
  const [mode, setMode] = useState('home');
  const [phase, setPhase] = useState(0);
  const [subStep, setSubStep] = useState(0);
  const [isFarmVideoPlaying, setIsFarmVideoPlaying] = useState(false);
  const farmVideoRef = useRef(null);

  // Phase 4: Biomarkers (URL-driven)
  const [bioOption, setBioOption] = useState(null);

  // Navigation/UI state
  const [faqOpen, setFaqOpen] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [dots, setDots] = useState(0);

  const ref = useRef(null);
  const menuRef = useRef(null);
  const faqRef = useRef(null);
  const scrollTop = () => ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
  const scrollToMenu = () => menuRef.current?.scrollIntoView({ behavior: 'smooth' });

  const { currentUser } = useUser();
  const hasActiveAccount = currentUser && currentUser.reorder;

  useEffect(() => {
    if (hasActiveAccount) {
      history.replace('/onramp/order-management');
    }
  }, [hasActiveAccount, history]);

  const { loading, data: { listHomepageMeals = [] } = {} } = useQuery(LIST_HOMEPAGE_MEALS);

  useEffect(() => {
    if (!document.getElementById('m-fonts')) {
      const l = document.createElement('link'); l.id = 'm-fonts'; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(l);
    }
  }, []);


  const pushPath = (nextPath, replace = false) => {
    const currentPath = normalizePath(location.pathname);
    const targetPath = normalizePath(nextPath);
    if (currentPath !== targetPath) {
      if (replace) {
        history.replace(targetPath);
      } else {
        history.push(targetPath);
      }
    }
    scrollTop();
  };

  const onrampPath = (nextPhase, nextSubStep = 0, nextBioOption = null) => {
    if (nextPhase === 3) return ROUTE_PATHS.onramp[3][nextSubStep] || ROUTE_PATHS.onramp[3][0];
    if (nextPhase === 4) {
      if (nextBioOption === 'upload') return ROUTE_PATHS.onramp[4].upload;
      if (nextBioOption === 'flag') return ROUTE_PATHS.onramp[4].flag;
      return ROUTE_PATHS.onramp[4].default;
    }
    if (nextPhase === 5) return ROUTE_PATHS.onramp[5][nextSubStep] || ROUTE_PATHS.onramp[5][0];
    if (nextPhase === 8) return ROUTE_PATHS.onramp[8][nextSubStep] || ROUTE_PATHS.onramp[8][0];
    return ROUTE_PATHS.onramp[nextPhase] || ROUTE_PATHS.onramp[0];
  };

  const startOnramp = () => pushPath(onrampPath(0));
  const goHome = () => pushPath(ROUTE_PATHS.home);
  const goPhase = (nextPhase, nextSubStep = 0, nextBioOption = null) => pushPath(onrampPath(nextPhase, nextSubStep, nextBioOption));
  // const goCheckoutStep = (nextStep) => pushPath(ROUTE_PATHS.checkout[nextStep] || ROUTE_PATHS.checkout[0]);

  useEffect(() => {
    const routeState = getRouteState(location.pathname);
    if (!routeState) {
      history.replace(ROUTE_PATHS.home);
      return;
    }

    setMode(routeState.mode);
    if (routeState.mode === 'onramp') {
      setPhase(routeState.phase);
      setSubStep(routeState.subStep);
      setBioOption(routeState.bioOption);
      if (routeState.checkoutStep !== undefined) {
        setCheckoutStep(routeState.checkoutStep);
      }
      return;
    }

    setBioOption(null);
    if (routeState.mode === 'checkout') {
      setCheckoutStep(routeState.checkoutStep);
    }
  }, [history, location.pathname]);


  // Scroll to top when phase, subStep, bioOption, or checkoutStep changes
  useEffect(() => {
    scrollTop();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [phase, subStep, bioOption, checkoutStep]);

  // Loading effect (must be before all conditional returns)
  useEffect(() => {
    if (mode !== 'loading') { setDots(0); return; }
    const t = setInterval(() => setDots((d) => d < 4 ? d + 1 : d), 800);
    const end = setTimeout(() => { history.replace(ROUTE_PATHS.results); }, 3500);
    return () => { clearInterval(t); clearTimeout(end); };
  }, [history, mode]);

  // Progress bar labels
  const progressLabels = ['Goals', 'Lifestyle', 'Taste', 'Biomarkers', 'Program', 'Plan', 'Your Menu'];

  // ═══════════════════════════════════════════════════
  // HOMEPAGE—v2 copy
  // ═══════════════════════════════════════════════════
  if (mode === 'home') {
    const reviews = [
      { name: 'Melissa Dry', text: "I've lost 8 lbs in 6 weeks and I don't feel deprived of anything. The portion sizes are great and the ingredients are definitely of the highest quality." },
      { name: 'Cassandra DePecol', text: "Super high-quality, delicious cuisine that's incredibly satisfying. My mom asks for Methodology for her birthday and Mother's Day every year without fail." },
      { name: 'Matthew Zehner', text: "Much more interesting than other meal prep deliveries I've tried. It feels cleaner and healthier, with a wide variety of plants and high-quality ingredients." },
      { name: 'Din LBC', text: "As a beef and potatoes kinda gal, I am blown away by how much I LOVE the seafood and even the vegetables. At first I considered Methodology a luxury. Now I don't want or need to go out for lunch or dinner." },
    ];
    const outcomes = [
      { name: 'Karen Cheng', text: "I've been eating Methodology six meals a week for a year. Just got my annual results back — cholesterol dropped from 188 to 153, triglycerides from 151 to 55. You make it so easy.", metric: 'Cholesterol 188 → 153', metric2: 'Triglycerides 151 → 55', label: 'ANNUAL BLOODWORK' },
      { name: 'Ojos N.', text: 'My husband was having trouble with his cholesterol and high blood pressure. Methodology has helped so much — he sleeps better, has more energy for workouts, and his numbers have improved.', metric: 'Cholesterol improved', metric2: 'Better sleep + energy', label: 'CHOLESTEROL + BLOOD PRESSURE' },
    ];
    const stanfordStudy = {
      title: 'Peer-reviewed and published in the "American Journal of Clinical Nutrition" for blood sugar control.',
      journal: 'Published in The American Journal of Clinical Nutrition, 2022',
      lead: 'Study Lead: Dr. Christopher Gardner',
      leadTitle: 'Director of Nutrition Studies',
      summary: 'When clinical researches needed meals precise enough for a randomized clinical trial on prediabetes and type 2 diabetes, they chose Methodology. For the first 4 weeks of each 12-week phase, all participant meals were provided by Methodology to ensure strict dietary adherence.',
      results: [
        { stat: '7–8%', label: 'average weight loss' },
        { stat: 'HbA1c', label: 'improved in both diet phases' },
        { stat: '40', label: 'participants with prediabetes or T2DM' },
      ],
      citation: 'Gardner CD, Landry MJ, et al. Am J Clin Nutr. 2022;116:640–652.',
      pubmedUrl: 'https://pubmed.ncbi.nlm.nih.gov/35641199/',
    };
    const faqs = [
      { q: "What's on the menu?", a: 'A rotating weekly menu of 20+ dishes with 30–65g protein per lunch/dinner meal. Breakfast, lunch, and dinner options. The menu changes every single week.' },
      { q: 'How does delivery work?', a: 'Nationwide delivery to all 48 contiguous states. Meals arrive Monday by 6AM (California courier) or by 5PM (FedEx everywhere else). No coordination necessary.' },
      { q: 'Can you accommodate my allergies?', a: "Everything is already gluten-free and refined-sugar-free. During signup, you'll select specific ingredients to avoid and we'll build your menu around them." },
      { q: "What if I don't like something?", a: "Swap any meal on your menu before the weekly lock. You're always in control of what shows up at your door." },
      { q: 'Is there a commitment?', a: "None. Skip weeks or cancel anytime with one week's notice from directly within your account settings." },
      { q: 'How much does it cost?', a: 'Plans start at 8 meals per week. Pricing varies by location—build your plan to see exact pricing for your area.' },
    ];

    const FooterLinks = [
      // { title: 'Our Story', url: 'https://gomethodology.com/our-story' },
      { title: 'FAQ', url: 'https://gomethodology.com/faq' } ,
      { title: 'Terms and Conditions', url: 'https://gomethodology.com/terms-conditions' },
    ];

    const RenderMeals = () => {
      if (loading) {
        return <Loading />;
      }
      return (
        <div className="m-menu-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
          {listHomepageMeals.map((m, i) =>
            <div key={i} style={{ background: C.pureWhite, border: '1px solid #E8E4DC', borderRadius: 0, padding: 14 }}>
              {m.photoUrl ? (
                <img
                  src={m.photoUrl}
                  alt={m.name}
                  style={{ width: '100%', height: 90, objectFit: 'contain', borderRadius: 0, marginBottom: 8, display: 'block' }}
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div style={{ height: 90, background: C.lightBeige, borderRadius: 0, marginBottom: 8, display: m.photoUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Brandmark size={20} color={C.lightBrown}/>
              </div>
              <div style={{ fontFamily: F.h, fontSize: 13, fontWeight: 500, color: C.darkBrown, marginBottom: 3, lineHeight: 1.2 }}>
                {m.name}
              </div>
              <div style={{ fontSize: 11, color: C.lightBrown }}>{m.calories} cal · {m.proteinG}g protein</div>
            </div>
          )}
        </div>
      );
    };

    return (
    <div ref={ref} className="m-root" style={{ fontFamily: F.b, color: C.darkBrown, background: C.white, minHeight: '100vh', overflowY: 'auto' }}>
      <style>{`
        @keyframes farmShimmer { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        @media (max-width: 768px) {
          .m-nav-links { display: none !important; }
          .m-nav-btn { padding: 7px 14px !important; font-size: 12px !important; }
          .m-hero { min-height: auto !important; flex-direction: column !important; }
          .m-hero-img { position: relative !important; width: 100% !important; height: 320px !important; inset: auto !important; object-position: center center !important; }
          .m-hero-overlay { display: none !important; }
          .m-hero-content { margin-left: 0 !important; max-width: 100% !important; padding: 32px 24px 40px !important; position: relative !important; bottom: auto !important; background: linear-gradient(180deg, rgba(44,36,24,0.95) 0%, rgba(44,36,24,1) 100%); }
          .m-hero-content h1 { font-size: 32px !important; }
          .m-section { padding: 48px 20px !important; }
          .m-section-heading { font-size: 28px !important; text-align: center !important; }
          .m-hiw-grid { flex-direction: column !important; }
          .m-diff-section { flex-direction: column !important; gap: 0 !important; padding: 48px 20px !important; }
          .m-diff-img { flex: none !important; width: 100% !important; }
          .m-diff-text { text-align: center !important; padding-left: 0 !important; padding-top: 24px !important; padding-bottom: 0 !important; align-items: center !important; justify-content: center !important; }
          .m-diff-text p { margin-left: auto !important; margin-right: auto !important; }
          .m-diff-inner { display: flex !important; flex-direction: column !important; align-items: center !important; width: 100% !important; }
          .m-stripes-edge { height: 24px !important; max-height: 24px !important; }
          .m-diff-stripes-right-top, .m-diff-stripes-right-bottom { display: none !important; }
          .m-diff-stripes-top { display: block !important; position: relative !important; height: 24px !important; max-height: 24px !important; width: 100% !important; }
          .m-diff-stripes-bottom { display: block !important; position: absolute !important; height: 24px !important; max-height: 24px !important; }
          .m-menu-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .m-standards-grid { flex-direction: column !important; gap: 28px !important; }
          .m-reviews-grid { grid-template-columns: 1fr !important; }
          .m-outcomes-grid { grid-template-columns: 1fr !important; }
          .m-stanford-stats { flex-direction: column !important; gap: 16px !important; }
          .m-farm-video { height: 200px !important; }
          .m-programs-grid { flex-direction: column !important; }
          .m-footer { flex-direction: column !important; gap: 28px !important; }
          .m-footer-links { gap: 24px !important; }
          .m-cta-features { flex-direction: column !important; gap: 8px !important; align-items: center !important; }
          .m-final-cta { flex-direction: column !important; min-height: auto !important; }
          .m-final-cta-img { flex: none !important; height: 700px !important; }
          .m-final-cta-copy { padding: 40px 28px !important; text-align: center !important; }
        }
      `}</style>
      <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 28px', borderBottom: '1px solid #EDE9E0', position: 'sticky', top: 0, background: C.white, zIndex: 100 }}>
        <div style={{ fontFamily: F.h, fontSize: 22, fontWeight: 600, color: C.darkBrown }}>Methodology</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
          <div className="m-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
            {[
              { label: "This Week's Menu", url: 'https://app.gomethodology.com/menu' },
              // { label: 'Our Story', url: 'https://gomethodology.com/our-story' },
              { label: 'FAQ', action: () => faqRef.current?.scrollIntoView({ behavior: 'smooth' }) },
            ].map((t) => t.action
              ? <span key={t.label} onClick={t.action} style={{ fontSize: 13, color: C.brown, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>{t.label}</span>
              : <a key={t.label} href={t.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: C.brown, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap', textDecoration: 'none' }}>{t.label}</a>)}
          </div>
          <span className="m-nav-links" onClick={() => history.push('/sign-in')} style={{ fontSize: 13, color: C.brown, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>Log In</span>
          <Btn className="m-nav-btn" onClick={startOnramp} style={{ padding: '8px 16px', fontSize: 13, whiteSpace: 'nowrap' }}>Build Your Plan</Btn>
        </div>
      </nav>

      {/* S1: Hero—lifestyle photo with text on right */}
      {/* minHeight: 520 (old) */}
      {/* m-hero-content maxWidth: 440 */}
      <div className="m-hero" style={{ position: 'relative', minHeight: 'calc(94vh)', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <img className="m-hero-img" src={IMAGES.HERO_IMG} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left top' }}/>
        <div className="m-hero-overlay" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(0,0,0,0) 30%, rgba(44,36,24,0.75) 55%, rgba(44,36,24,0.92) 75%)' }}/>
        <div className="m-hero-content" style={{ position: 'relative', zIndex: 1, marginLeft: '54%', maxWidth: '50%', padding: '60px 48px 60px 20px', width: '100%' }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '1.8px', color: C.lightGreen, marginBottom: 18 }}>NO GLUTEN · NO CANOLA OIL · NO REFINED SUGAR · DELIVERED NATIONWIDE</div>
          <h1 style={{ fontFamily: F.h, fontSize: 42, fontWeight: 500, color: C.pureWhite, lineHeight: 1.08, margin: '0 0 16px', textWrap: 'balance' }}>{'Your food should work as hard as\u00A0you\u00A0do.'}</h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: '0 0 28px' }}>{'Meals built around your goals, tastes, and (if you want) actual bloodwork. Ready in five minutes.'}</p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
            <Btn onClick={startOnramp} style={{ background: C.lightGreen, color: C.darkBrown }}>Build Your Plan →</Btn>
            <span onClick={scrollToMenu} style={{ fontSize: 14, color: C.lightGreen, cursor: 'pointer', fontWeight: 500, borderBottom: '1px solid rgba(186,190,131,0.4)' }}>See This Week&#39;s Menu</span>
          </div>
        </div>
      </div>

      {/* S2: Trust Bar—animated ticker */}
      <div style={{ overflow: 'hidden', borderBottom: '1px solid #EDE9E0', background: C.pureWhite, padding: '14px 0' }}>
        <style>{'@keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }'}</style>
        <div style={{ display: 'flex', animation: 'ticker 30s linear infinite', width: 'max-content' }}>
          {[0, 1, 2, 3].map((i) => <div key={i} style={{ display: 'flex', gap: 12, paddingRight: 12 }}>
            {['9,000+ Rotating Recipes', '200+ Plants Per Week', 'Farm to Kitchen in 48 Hours', '30–65g Protein Per Lunch & Dinner', 'Ships to 48 States'].map((t) => <span key={`${i}-${t}`} style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', color: C.brown, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 12 }}>{t}<span style={{ color: C.lightBrown }}>·</span></span>)}
          </div>)}
        </div>
      </div>

      {/* S3: The Shift */}
      <div className="m-section m-diff-section" style={{ padding: '72px 28px', maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'stretch', gap: 0, position: 'relative' }}>
        {/* Desktop: vertical stripes behind text area, aligned to image top/bottom. Mobile: horizontal top/bottom bars */}
        {/* Desktop: horizontal stripes above and below text, aligned to image top/bottom, right side only */}
        <div className="m-diff-stripes-right-top" style={{ position: 'absolute', top: 72, left: '45%', right: 0, height: 48, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, backgroundImage: `repeating-linear-gradient(90deg, ${C.lightBrown} 0px, ${C.lightBrown} 3px, transparent 3px, transparent 13px)`, opacity: 0.08 }}/>
        <div className="m-diff-stripes-right-bottom" style={{ position: 'absolute', bottom: 72, left: '45%', right: 0, height: 48, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, backgroundImage: `repeating-linear-gradient(90deg, ${C.lightBrown} 0px, ${C.lightBrown} 3px, transparent 3px, transparent 13px)`, opacity: 0.08 }}/>
        <div className="m-diff-img" style={{ flex: '0 0 45%', overflow: 'hidden', position: 'relative', zIndex: 2 }}>
          <img src={IMAGES.DIFF_IMG} alt="Methodology meal" style={{ width: '100%', height: 520, objectFit: 'cover', display: 'block' }}/>
        </div>
        <div className="m-stripes-edge m-diff-stripes-top" style={{ display: 'none', position: 'relative', width: '100%', height: 24, pointerEvents: 'none', overflow: 'hidden', zIndex: 3, backgroundImage: `repeating-linear-gradient(90deg, ${C.lightBrown} 0px, ${C.lightBrown} 3px, transparent 3px, transparent 13px)`, opacity: 0.08 }}/>
        <div className="m-stripes-edge m-diff-stripes-bottom" style={{ display: 'none', position: 'absolute', bottom: 0, left: 0, right: 0, height: 24, pointerEvents: 'none', overflow: 'hidden', zIndex: 3, backgroundImage: `repeating-linear-gradient(90deg, ${C.lightBrown} 0px, ${C.lightBrown} 3px, transparent 3px, transparent 13px)`, opacity: 0.08 }}/>
        <div className="m-diff-text" style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', paddingLeft: 56 }}>
          <div className="m-diff-inner" style={{ position: 'relative', zIndex: 1 }}>
            <Eyebrow>THE METHODOLOGY DIFFERENCE</Eyebrow>
            <Heading className="m-section-heading" size={38} style={{ marginBottom: 16 }}>Every meal engineered with protein, fiber, prebiotics, omega-3s, and slow-digesting carbs.</Heading>
            <p style={{ fontSize: 14, color: C.brown, lineHeight: 1.65, maxWidth: 500, marginBottom: 28 }}>30-65g protein per lunch and dinner. 200+ plants per week. Designed for gut health, focus, energy, and body composition.</p>
            <Btn onClick={startOnramp} style={{ background: C.yellow, color: C.darkBrown }}>Build Your Personalized Plan →</Btn>
          </div>
        </div>
      </div>

      {/* S4: How It Works */}
      <div style={{ background: C.lightBeige, padding: '64px 28px', borderTop: '1px solid #EDE9E0', borderBottom: '1px solid #EDE9E0' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}><Eyebrow>HOW IT WORKS</Eyebrow></div>
          <Heading size={34} style={{ textAlign: 'center', marginBottom: 36 }}>Three minutes to a plan built around you.</Heading>
          <div className="m-hiw-grid" style={{ display: 'flex', gap: 24 }}>
            {[{ n: '01', t: 'Tell Us About You', d: 'Your goals, your taste, ingredients to avoid. Have bloodwork? Share it.' },
              { n: '02', t: 'We Match Every Meal to You', d: 'High cholesterol? More omega-3s. On a GLP-1? 100+g protein a day. The more we know, the sharper our meal matching becomes.' },
              { n: '03', t: 'Heat, Eat, Repeat', d: 'Glass jars. Ready in five minutes. No prep, no thinking. New menu every week.' },
            ].map((s) => <div key={s.n} style={{ flex: 1 }}><div style={{ fontSize: 28, fontWeight: 300, color: C.lightGreen, fontFamily: F.h, marginBottom: 10 }}>{s.n}</div><div style={{ fontSize: 15, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>{s.t}</div><div style={{ fontSize: 13, color: C.brown, lineHeight: 1.6 }}>{s.d}</div></div>)}
          </div>
          <div style={{ textAlign: 'center', marginTop: 36 }}><Btn onClick={startOnramp}>Get Started—It's Free to Build a Plan →</Btn></div>
        </div>
      </div>

      {/* S5: Menu Placeholder */}
      <div ref={menuRef} style={{ padding: '64px 28px', maxWidth: 800, margin: '0 auto' }}>
        <div style={{ textAlign: 'center' }}><Eyebrow>NEXT WEEK</Eyebrow></div>
        <Heading size={34} style={{ textAlign: 'center', marginBottom: 10 }}>Meals designed to consider your biomarkers, not just fill your plate.</Heading>
        <p style={{ fontSize: 14, color: C.brown, lineHeight: 1.6, textAlign: 'center', maxWidth: 560, margin: '0 auto 28px', textWrap: 'balance' }}>A rotating weekly menu of 20+ dishes with 30-65g protein per lunch and dinner meal across Standard, Large, and XL portions. Macros shown below are for Standard.</p>
        <RenderMeals />
        <div style={{ textAlign: 'center', marginTop: 28 }}><Btn onClick={startOnramp} style={{ background: C.yellow, color: C.darkBrown }}>Build Your Plan →</Btn></div>
      </div>

      {/* S6: Standards — with farm video */}
      <div style={{ background: C.darkBrown, position: 'relative', overflow: 'hidden' }}>
        <StripeTexture color="#000000" opacity={0.10}/>
        <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
          <video
            ref={farmVideoRef}
            src="https://d1cbtwb0qnacuk.cloudfront.net/videos/HomepageVideo_Methodology_Stephen_s_Field_Notes_Final_16x9.mp4#t=0.1"
            controls={isFarmVideoPlaying}
            playsInline
            preload="metadata"
            onPlay={() => setIsFarmVideoPlaying(true)}
            onPause={() => setIsFarmVideoPlaying(false)}
            onEnded={() => setIsFarmVideoPlaying(false)}
            style={{ width: '100%', display: 'block' }}
          />
          {!isFarmVideoPlaying && (
            <div
              onClick={() => { if (farmVideoRef.current) farmVideoRef.current.play().catch(() => {}); }}
              style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2, cursor: 'pointer', background: 'linear-gradient(180deg, rgba(59,46,34,0) 0%, rgba(59,46,34,0.15) 50%, rgba(59,46,34,0.85) 100%)' }}
            >
              <div style={{ width: 60, height: 60, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(4px)' }}>
                <div style={{ width: 0, height: 0, borderLeft: '18px solid rgba(255,255,255,0.85)', borderTop: '11px solid transparent', borderBottom: '11px solid transparent', marginLeft: 5 }}/>
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '2.5px', color: 'rgba(255,255,255,0.65)' }}>OUR FARM · OUR KITCHEN · YOUR TABLE</div>
            </div>
          )}
        </div>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 720, margin: '0 auto', padding: '48px 28px 64px' }}>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '2px', color: C.lightGreen, marginBottom: 10 }}>OUR STANDARDS</div></div>
          <h2 style={{ fontFamily: F.h, fontSize: 34, fontWeight: 500, color: C.pureWhite, textAlign: 'center', margin: '0 0 36px', textWrap: 'balance' }}>What we put in. What we leave out.</h2>
          <div className="m-standards-grid" style={{ display: 'flex', gap: 48, justifyContent: 'center' }}>
            <div><div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: C.lightGreen, marginBottom: 14 }}>ALWAYS</div>{['Pastured, wild, and sustainable proteins — for bioavailable iron and complete aminos', 'Cold-pressed extra virgin olive oil — shown to lower LDL and inflammation markers', 'Avocado oil only (never seed oils) — stable at high heat, high in oleic acid', 'Produce from our farm, in the kitchen within 48 hours — peak nutrient density', 'Every ingredient weighed to the gram — so your macros are exact, not estimated'].map((t) => { const [main, reason] = t.split(' — '); return <div key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}><Check size={14} color={C.lightGreen} strokeWidth={2.5} style={{ marginTop: 2, flexShrink: 0 }}/><span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{main}<span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}> — {reason}</span></span></div>; })}</div>
            <div><div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: C.concern, marginBottom: 14 }}>NEVER</div>{['Gluten or wheat — linked to gut permeability and inflammation', 'Refined sugar — spikes insulin, disrupts metabolic health', 'Canola, soybean, or vegetable oil — high omega-6, promotes inflammation', 'Emulsifiers, stabilizers, or fillers — disrupt gut microbiome', "Anything you'd need a chemistry degree to pronounce"].map((t) => { const parts = t.split(' — '); const main = parts[0]; const reason = parts[1]; return <div key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}><X size={14} color={C.concern} strokeWidth={2.5} style={{ marginTop: 2, flexShrink: 0 }}/><span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{main}{reason && <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}> — {reason}</span>}</span></div>; })}</div>
          </div>
        </div>
      </div>

      {/* S7: Social Proof — Stanford Study + Outcomes + Reviews */}
      <div style={{ padding: '64px 28px', maxWidth: 800, margin: '0 auto' }}>
        <div style={{ textAlign: 'center' }}><Eyebrow>REAL RESULTS</Eyebrow></div>
        <Heading size={30} style={{ textAlign: 'center', marginBottom: 28 }}>The bloodwork doesn't lie.</Heading>

        {/* Stanford Study — Featured Research Callout */}
        <div style={{ background: C.darkBrown, padding: 32, marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: C.mustard }}/>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: C.mustard }}>PEER-REVIEWED RESEARCH</div>
            <div style={{ height: 1, flex: 1, background: 'rgba(255,255,255,0.1)' }}/>
          </div>
          <h3 style={{ fontFamily: F.h, fontSize: 22, fontWeight: 500, color: C.pureWhite, margin: '0 0 12px', lineHeight: 1.3, textWrap: 'balance' }}>{stanfordStudy.title}</h3>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.65, margin: '0 0 20px' }}>{stanfordStudy.summary}</p>
          <div className="m-stanford-stats" style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
            {stanfordStudy.results.map((r, i) => <div key={i} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontFamily: F.h, fontSize: 22, fontWeight: 500, color: C.mustard, marginBottom: 2 }}>{r.stat}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.3px', lineHeight: 1.3 }}>{r.label}</div>
            </div>)}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>Lead: <span style={{ color: 'rgba(255,255,255,0.85)' }}>{stanfordStudy.lead}</span></div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{stanfordStudy.leadTitle}</div>
            </div>
            <a href={stanfordStudy.pubmedUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.mustard, textDecoration: 'none', borderBottom: '1px solid rgba(196,162,101,0.3)', paddingBottom: 1, whiteSpace: 'nowrap' }}>{stanfordStudy.journal} →</a>
          </div>
        </div>

        {/* Outcome testimonials — cholesterol + blood sugar */}
        <div className="m-outcomes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 32 }}>
          {outcomes.map((o, i) => <div key={i} style={{ background: C.pureWhite, border: '1px solid #E8E4DC', padding: 24, position: 'relative' }}>
            <div style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: C.pureWhite, background: C.darkBrown, padding: '4px 10px', marginBottom: 14 }}>{o.label}</div>
            <div style={{ fontFamily: F.h, fontSize: 24, fontWeight: 500, color: C.darkBrown, marginBottom: 4, lineHeight: 1.15 }}>{o.metric}</div>
            {o.metric2 && <div style={{ fontFamily: F.h, fontSize: 18, fontWeight: 500, color: C.brown, marginBottom: 12, lineHeight: 1.15 }}>{o.metric2}</div>}
            <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.6, margin: '0 0 14px', fontStyle: 'italic' }}>"{o.text}"</p>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown }}>— {o.name}</div>
            {o.note && <div style={{ fontSize: 9, color: C.concern, marginTop: 8, fontWeight: 600, letterSpacing: '0.5px' }}>{o.note}</div>}
          </div>)}
        </div>
        {/* General reviews */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}><div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '2px', color: C.lightBrown }}>★★★★★ 4.8 ON GOOGLE</div></div>
        <div className="m-reviews-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {reviews.map((r, i) => <div key={i} style={{ background: C.pureWhite, border: '1px solid #E8E4DC', borderRadius: 0, padding: 20 }}><div style={{ fontSize: 13, color: C.mustard, marginBottom: 8 }}>{'★'.repeat(5)}</div><p style={{ fontSize: 13, color: C.brown, lineHeight: 1.6, margin: '0 0 12px', fontStyle: 'italic' }}>"{r.text}"</p><div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown }}>{r.name}</div></div>)}
        </div>
        <div style={{ textAlign: 'center', marginTop: 20 }}><a href="https://maps.app.goo.gl/Ek569kch4itZaag86" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, color: C.brown, textDecoration: 'none', borderBottom: `1px solid ${C.lightBrown}`, paddingBottom: 1 }}>Read on Google →</a></div>
      </div>
      <div style={{ background: C.lightBeige, padding: '64px 28px', borderTop: '1px solid #EDE9E0' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}><Eyebrow>FIND YOUR FIT</Eyebrow></div>
          <Heading size={34} style={{ textAlign: 'center', marginBottom: 32 }}>Your goals are specific. Your program should be, too.</Heading>
          <div className="m-programs-grid" style={{ display: 'flex', gap: 16 }}>
            {[{ t: 'Signature', ic: 'sparkle', img: IMAGES.SIG_IMG, pos: 'center 20%', d: '35-50g protein per lunch/dinner meal, calorie-controlled, designed to transform your body composition, energy, and mood, without ever feeling like a diet. The program that built our reputation.' },
              { t: 'Sustain GLP-1', ic: 'shield', img: IMAGES.SUSTAIN_IMG, pos: 'center 30%', d: 'On Ozempic, Wegovy, or Mounjaro? The medication handles your appetite. We handle the nutrition. 100-140g protein per day, portioned for suppressed appetites, formulated to minimize nausea and bloating.' },
              { t: 'Peak', ic: 'mountain', img: IMAGES.PEAK_IMG, pos: 'center 20%', d: "Built for athletes and serious trainers. 45-65g protein per meal, complex carbs for glycogen recovery, leaner fat profiles for faster digestion. This isn't bodybuilder food. It's what elite nutrition actually looks like." },
            ].map((p, i) => <div key={i} style={{ flex: 1, background: C.pureWhite, borderRadius: 0, overflow: 'hidden', border: '1px solid #E8E4DC' }}>
              <div style={{ height: 180, background: C.lightBeige, overflow: 'hidden' }}>
                {p.img ? <img src={p.img} alt={p.t} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: p.pos }}/> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Brandmark size={28} color={C.lightBrown}/></div>}
              </div>
              <div style={{ padding: '16px 20px 20px' }}>
                <div style={{ fontFamily: F.h, fontSize: 22, fontWeight: 500, color: C.darkBrown, margin: '0 0 8px' }}>{p.t}</div>
                <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.6, margin: 0 }}>{p.d}</p>
              </div>
            </div>)}
          </div>
          <div style={{ textAlign: 'center', marginTop: 28 }}>
            <Btn onClick={startOnramp}>Not sure? Build Your Plan and we'll recommend one →</Btn>
          </div>
        </div>
      </div>

      {/* S9: FAQ */}
      <div ref={faqRef} style={{ padding: '64px 28px', maxWidth: 620, margin: '0 auto' }}>
        <Heading size={30} style={{ textAlign: 'center', marginBottom: 28 }}>Quick answers.</Heading>
        {faqs.map((f, i) => <div key={i} style={{ borderBottom: '1px solid #E8E4DC' }}><button onClick={() => setFaqOpen(faqOpen === i ? null : i)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '16px 0', background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.b, textAlign: 'left' }}><span style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown }}>{f.q}</span><ChevronDown size={16} color={C.lightBrown} style={{ transform: faqOpen === i ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}/></button>{faqOpen === i && <div style={{ fontSize: 13, color: C.brown, lineHeight: 1.6, paddingBottom: 16 }}>{f.a}</div>}</div>)}
      </div>

      {/* S10: Final CTA */}
      <div className="m-final-cta" style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'stretch', minHeight: 380 }}>
        <div className="m-final-cta-img" style={{ flex: '0 0 38%', overflow: 'hidden' }}>
          <img src={IMAGES.STEVE_IMG} alt="Enjoying Methodology meals" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}/>
        </div>
        <div className="m-final-cta-copy" style={{ flex: 1, position: 'relative', background: C.darkBrown, padding: '56px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
          <StripeTexture color="#000000" opacity={0.10}/>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{ fontFamily: F.h, fontSize: 30, fontWeight: 500, color: C.pureWhite, lineHeight: 1.15, margin: '0 0 14px', textWrap: 'balance' }}>Stop thinking about food. Start letting it work for you.</h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: '0 0 28px' }}>Build a plan in three minutes. Personalized to your goals, your taste, your biology. Delivered in glass jars. Ready in five.</p>
            <Btn onClick={startOnramp} style={{ background: C.yellow, color: C.darkBrown }}>Build Your Plan →</Btn>
            <div className="m-cta-features" style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 20 }}>
              {['No commitment', 'Skip or cancel anytime', 'Swap meals'].map((t) => <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Check size={12} color={C.lightGreen} strokeWidth={2.5}/><span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{t}</span></div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="m-footer" style={{ padding: '36px 28px', borderTop: '1px solid #EDE9E0', display: 'flex', justifyContent: 'space-between', maxWidth: 900, margin: '0 auto' }}>
        <div><div style={{ fontFamily: F.h, fontSize: 18, fontWeight: 600, color: C.darkBrown, marginBottom: 8 }}>Methodology</div><div style={{ fontSize: 11, color: C.lightBrown }}>© 2026 Methodology Inc. All rights reserved.</div></div>
        <div className="m-footer-links" style={{ display: 'flex', gap: 40 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1px', color: C.darkBrown, marginBottom: 8 }}>EXPLORE</div>{FooterLinks.map((t, index) =>
              <div key={index}
                style={{ fontSize: 12, color: C.brown, marginBottom: 5, cursor: 'pointer' }}>
                <a href={t.url} rel="noreferrer" target='_blank'>{t.title}</a>
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1px', color: C.darkBrown, marginBottom: 8 }}>SUPPORT</div>{[{ title: 'help@gomethodology.com', isEmail: true }].map((t) => <div key={t.title} style={{ fontSize: 12, color: C.brown, marginBottom: 5, cursor: 'pointer' }}>{t.isEmail ? <a href={`mailto:${t.title}`} style={{ color: C.brown, textDecoration: 'none' }}>{t.title}</a> : t.title}</div>)}</div>
        </div>
      </div>
    </div>);
  }

  const DefaultNavigation = () => {
    if (phase === 8) {
      return (
        <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 28px', borderBottom: '1px solid #EDE9E0' }}>
          <div style={{ fontFamily: F.h, fontSize: 20, fontWeight: 600, color: C.darkBrown }}>Methodology</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Lock size={14} color={C.lightBrown}/><span style={{ fontSize: 12, color: C.lightBrown }}>Secure checkout</span></div>
        </nav>
      );
    } else {
      return (
        <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid #EDE9E0', position: 'sticky', top: 0, background: C.white, zIndex: 100 }}>
          <div style={{ fontFamily: F.h, fontSize: 18, fontWeight: 600, color: C.darkBrown, cursor: 'pointer' }} onClick={goHome}>Methodology</div>
          {phase > 0 && phase <= 7 && <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: C.darkBrown }}>{progressLabels[Math.min(phase - 1, 6)]}</span>
            <div style={{ display: 'flex', gap: 3 }}>{progressLabels.map((_, i) => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i < phase ? C.darkBrown : i === phase - 1 ? C.green : '#E8E4DC', transition: 'all 0.2s' }}/>)}</div>
          </div>}
          {phase > 0 && <button onClick={() => { if (phase === 3 && subStep > 0) goPhase(3, subStep - 1); else if (phase === 8) goPhase(6, 0); else goPhase(phase - 1, 0); }} style={{ fontSize: 13, fontWeight: 600, color: C.brown, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.b, display: 'flex', alignItems: 'center', gap: 4 }}><ChevronLeft size={15}/> Back</button>}
        </nav>
      );
    }
  };

  return (
    <div ref={ref} style={{ fontFamily: F.b, color: C.darkBrown, background: C.white, minHeight: '100vh', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      {/* STICKY HEADER */}
      <DefaultNavigation />

      <div style={{ flex: 1, maxWidth: 600, margin: '0 auto', width: '100%', padding: '24px 20px' }}>
        <Fade key={`${phase}-${subStep}-${bioOption}`}>

        {phase === 0 && <Phase0Landing
          goPhase={goPhase}
        />}

        {phase === 1 && <Phase1Goals
          goPhase={goPhase}
        />}

        {phase === 2 && <Phase2Lifestyle
          goPhase={goPhase}
        />}

        {phase === 3 && <Phase3Taste
          subStep={subStep}
          goPhase={goPhase}
        />}

        {phase === 4 && <Phase4Biomarkers
          bioOption={bioOption}
          goPhase={goPhase}
        />}

        {phase === 5 && <Phase5Program
          subStep={subStep}
          bioOption={bioOption}
          goPhase={goPhase}
        />}

        {phase === 6 && <Phase6PlanConfig
          dots={dots}
          goPhase={goPhase}
        />}

        {phase === 7 && <Phase7Results
          goPhase={goPhase}
          startOnramp={startOnramp}
          bioOption={bioOption}
          scrollTop={scrollTop}
        />}

        {phase === 8 && <Phase8Checkout
          checkoutStep={checkoutStep}
          scrollTop={scrollTop}
          setMode={setMode}
          goPhase={goPhase}
        />}

        </Fade>
      </div>
    </div>
  );
}
